import { useState, useCallback } from 'react'
import { AdvisorAssignmentSection } from './AdvisorAssignmentSection'
import { DummyLLMAssignmentSection } from './DummyLLMAssignmentSection'
import { PlayerAssignmentSection } from './PlayerAssignmentSection'
import { TeamNameCell } from './TeamNameCell'
import ConfirmationModal from '../OGE/OGEDeletionModal'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import CreateRoomModal from '../Room/CreateRoomModal'
import { ModeType, RoomMember, RoomModalMode } from '@/redux/types/room'
import { Team } from '@/redux/types/team'
import { Advisor } from '@/redux/types/advisor'

interface TeamsMobileViewProps {
  teams: Team[]
  advisors: Advisor[]
  players: RoomMember[]
  isAdvisorAssignedToTeam: (advisorId: string, teamId: number) => boolean
  isPlayerAssignedToTeam: (playerId: string, teamId: number) => boolean
  onAdvisorToggle: (teamId: number, advisorId: string) => void
  onPlayerToggle: (teamId: number, playerId: string) => void
  dummyBots: Array<{ id: number; name: string }>
  isDummyAssignedToTeam: (dummyBotId: number, teamId: number) => boolean
  onDummyToggle: (teamId: number, dummyBotId: number) => void
  onUpdateTeam: (teamId: number, newName: string, description?: string) => Promise<void>
  onDeleteTeam: (teamId: number) => Promise<void>
  getTeamMemberCount: (teamId: number) => {
    players: number
    advisors: number
    total: number
  }
  hasTeamMembers: (teamId: number) => boolean
  isReadOnly?: boolean
}

export function TeamsMobileView({
  teams,
  advisors,
  players,
  isAdvisorAssignedToTeam,
  isPlayerAssignedToTeam,
  onAdvisorToggle,
  onPlayerToggle,
  dummyBots,
  isDummyAssignedToTeam,
  onDummyToggle,
  onUpdateTeam,
  onDeleteTeam,
  getTeamMemberCount,
  hasTeamMembers,
  isReadOnly = false,
}: TeamsMobileViewProps) {
  const [editModalState, setEditModalState] = useState<{
    isOpen: boolean
    teamId: number | null
    teamName: string
    teamDescription: string
    isLoading: boolean
  }>({
    isOpen: false,
    teamId: null,
    teamName: '',
    teamDescription: '',
    isLoading: false,
  })

  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean
    teamId: number | null
    teamName: string
  }>({
    isOpen: false,
    teamId: null,
    teamName: '',
  })

  const handleDeleteTeamClick = useCallback(
    (teamId: number) => {
      const team = teams.find((t) => t.id === teamId)
      if (team) {
        setDeleteModalState({
          isOpen: true,
          teamId,
          teamName: team.name,
        })
      }
    },
    [teams]
  )

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModalState({ isOpen: false, teamId: null, teamName: '' })
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteModalState.teamId) {
      await onDeleteTeam(deleteModalState.teamId)
    }
  }, [deleteModalState.teamId, onDeleteTeam])

  const handleEditTeamClick = useCallback(
    (teamId: number) => {
      const team = teams.find((t) => t.id === teamId)
      if (team) {
        setEditModalState({
          isOpen: true,
          teamId,
          teamName: team.name,
          teamDescription: team.description || '',
          isLoading: false,
        })
      }
    },
    [teams]
  )

  const handleEditModalClose = useCallback(() => {
    setEditModalState({
      isOpen: false,
      teamId: null,
      teamName: '',
      teamDescription: '',
      isLoading: false,
    })
  }, [])

  const handleEditConfirm = useCallback(
    async (newName: string, _timerMode?: unknown, description?: string) => {
      if (editModalState.teamId) {
        setEditModalState((prev) => ({ ...prev, isLoading: true }))
        try {
          await onUpdateTeam(editModalState.teamId, newName, description)
          handleEditModalClose()
        } catch (error) {
          console.error('Failed to update team:', error)
          setEditModalState((prev) => ({ ...prev, isLoading: false }))
        }
      }
    },
    [editModalState.teamId, onUpdateTeam, handleEditModalClose]
  )

  return (
    <div className='lg:hidden space-y-6'>
      <Card>
        <CardHeader className='pb-3'>
          <h3 className='text-lg font-semibold text-gray-700'>Teams</h3>
        </CardHeader>
        <CardContent>
          <div className='space-y-3'>
            {teams.map((team) => (
              <Card key={team.id} className='border border-gray-200'>
                <CardHeader className='pb-2'>
                  <TeamNameCell
                    teamId={team.id}
                    teamName={team.name}
                    onEditTeam={handleEditTeamClick}
                    onDeleteTeam={handleDeleteTeamClick}
                    className='justify-start'
                    isReadOnly={isReadOnly}
                  />
                </CardHeader>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <AdvisorAssignmentSection
        advisors={advisors}
        teams={teams}
        isAdvisorAssignedToTeam={isAdvisorAssignedToTeam}
        onAdvisorToggle={onAdvisorToggle}
        isMobile={true}
        isReadOnly={isReadOnly}
      />

      <DummyLLMAssignmentSection
        dummyBots={dummyBots}
        teams={teams}
        isDummyAssignedToTeam={isDummyAssignedToTeam}
        onDummyToggle={onDummyToggle}
        isMobile={true}
        isReadOnly={isReadOnly}
      />
      <PlayerAssignmentSection
        players={players}
        teams={teams}
        isPlayerAssignedToTeam={isPlayerAssignedToTeam}
        onPlayerToggle={onPlayerToggle}
        isMobile={true}
        isReadOnly={isReadOnly}
      />

      {!isReadOnly && (
        <>
          <CreateRoomModal
            modeType={ModeType.TEAM}
            isOpen={editModalState.isOpen}
            onClose={handleEditModalClose}
            onConfirm={handleEditConfirm}
            isLoading={editModalState.isLoading}
            mode={RoomModalMode.EDIT}
            currentRoomName={editModalState.teamName}
            currentDescription={editModalState.teamDescription}
          />

          <ConfirmationModal
            isOpen={deleteModalState.isOpen}
            onClose={handleDeleteModalClose}
            onConfirm={handleDeleteConfirm}
            title='Delete Team'
            description={
              deleteModalState.teamId && hasTeamMembers(deleteModalState.teamId)
                ? `Are you sure you want to delete "${deleteModalState.teamName}"? This team has ${getTeamMemberCount(deleteModalState.teamId).total} member(s) assigned. Deleting will unassign them.`
                : `Are you sure you want to delete "${deleteModalState.teamName}"? This action cannot be undone.`
            }
            confirmButtonText='Delete'
            cancelButtonText='Cancel'
          />
        </>
      )}
    </div>
  )
}
